import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from "@/lib/services/community";
import { useAuth } from "@/context/AuthContext";

const KEYS = {
  unreadCount: ["notifications", "unread-count"] as const,
  list: ["notifications", "list"] as const,
};

/**
 * Polls unread notification count every 30s.
 * Only runs when the user is logged in.
 */
export function useUnreadCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: KEYS.unreadCount,
    queryFn: getUnreadCount,
    enabled: !!user,
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
    staleTime: 15_000,
  });
}

/**
 * Fetches the full notification list.
 * Meant to be enabled when the notification panel is open.
 */
export function useNotificationList(enabled: boolean) {
  const { user } = useAuth();

  return useQuery({
    queryKey: KEYS.list,
    queryFn: getNotifications,
    enabled: !!user && enabled,
    staleTime: 10_000,
  });
}

/**
 * Marks a single notification as read.
 * Optimistically updates both list and unread count.
 */
export function useMarkRead() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list });
      qc.invalidateQueries({ queryKey: KEYS.unreadCount });
    },
  });
}

/**
 * Marks all notifications as read.
 */
export function useMarkAllRead() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list });
      qc.invalidateQueries({ queryKey: KEYS.unreadCount });
    },
  });
}
